'use client';

import { useEffect, useRef, useState } from 'react';
import type { Chart as ChartType, Plugin } from 'chart.js';
import { A2_ALPHA, NON_ALPHA, lblColor, solidColor, TOOLTIP_STYLE } from './chartTheme';
import ChartHeader from './ChartHeader';
import Logo from '@/components/icons/Logo';

type EloRow = {
  name: string;
  a2: boolean;
  elo: number;
  std: number;
  optional?: 'nano';
};

const eloData: EloRow[] = [
  { name: 'Line 6 Proxy', a2: false, elo: 866.0164594751343, std: 89.1796610644661 },
  { name: 'NAM A1-Nano', a2: false, elo: 1105.1002565858255, std: 56.37044201107569, optional: 'nano' },
  { name: 'TONEX', a2: false, elo: 1376.5903643149938, std: 35.24676028794981 },
  { name: 'Neural DSP V2', a2: false, elo: 1389.3248637511954, std: 25.780310569185335 },
  { name: 'NAM A2-Lite', a2: true, elo: 1485.0112516299125, std: 53.80945182691625 },
  { name: 'NAM A1-Standard', a2: false, elo: 1833.6142073883161, std: 38.6476200293294 },
  { name: 'NAM A2-Full', a2: true, elo: 2038.9963795656931, std: 81.48453978425519 },
];

export default function A2Elo() {
  const eloRef = useRef<HTMLCanvasElement | null>(null);
  const [includeEloNano, setIncludeEloNano] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let chart: ChartType | undefined;

    (async () => {
      const { Chart, registerables } = await import('chart.js');
      if (cancelled) return;
      Chart.register(...registerables);

      if (!eloRef.current) return;

      const filtered = eloData.filter(
        (d) => d.optional !== 'nano' || includeEloNano,
      );
      const eloColors = filtered.map((d) => (d.a2 ? A2_ALPHA(0.25) : NON_ALPHA(0.25)));
      const eloBorderColors = filtered.map((d) => solidColor(d));
      const eloErrColors = filtered.map((d) => solidColor(d));
      const eloLblColors = filtered.map((d) => lblColor(d));

      const errorBarPlugin: Plugin<'bar'> = {
        id: 'eloErrorBar',
        afterDatasetsDraw(c) {
          const ctx = c.ctx;
          const xAxis = c.scales.x;
          const yAxis = c.scales.y;
          const capW = 6;
          ctx.save();
          ctx.lineWidth = 2;
          filtered.forEach((d, i) => {
            const cx = xAxis.getPixelForValue(i);
            const yLo = yAxis.getPixelForValue(d.elo - d.std);
            const yHi = yAxis.getPixelForValue(d.elo + d.std);
            const yMid = yAxis.getPixelForValue(d.elo);
            ctx.strokeStyle = eloErrColors[i];
            ctx.globalAlpha = d.a2 ? 0.35 : 1;
            ctx.beginPath();
            ctx.moveTo(cx, yLo);
            ctx.lineTo(cx, yHi);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(cx - capW, yHi);
            ctx.lineTo(cx + capW, yHi);
            ctx.stroke();
            ctx.beginPath();
            ctx.moveTo(cx - capW, yLo);
            ctx.lineTo(cx + capW, yLo);
            ctx.stroke();
            ctx.globalAlpha = 1;
            ctx.fillStyle = d.a2 ? eloErrColors[i] : 'rgba(160,160,168,0.9)';
            ctx.beginPath();
            ctx.arc(cx, yMid, 3, 0, Math.PI * 2);
            ctx.fill();
            ctx.font = '13px Arial, sans-serif';
            ctx.fillStyle = eloLblColors[i];
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            ctx.fillText(String(Math.round(d.elo)), cx, yHi - 6);
          });
          ctx.restore();
        },
      };

      chart = new Chart(eloRef.current, {
        type: 'bar',
        data: {
          labels: filtered.map((d) => d.name),
          datasets: [
            {
              data: filtered.map((d) => d.elo),
              backgroundColor: eloColors,
              borderColor: eloBorderColors,
              borderWidth: 1,
              borderRadius: 3,
              barThickness: 36,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: { duration: 800, easing: 'easeOutQuart' },
          interaction: { mode: 'nearest', axis: 'x', intersect: false },
          plugins: {
            legend: { display: false },
            tooltip: {
              ...TOOLTIP_STYLE,
              callbacks: {
                title: (items) => filtered[items[0].dataIndex].name,
                label: (ctx) => {
                  const d = filtered[ctx.dataIndex];
                  return [
                    `Elo: ${Math.round(d.elo)}`,
                    `Std: ±${Math.round(d.std)}`,
                  ];
                },
              },
            },
          },
          scales: {
            x: {
              grid: { display: false },
              ticks: {
                font: { family: 'Arial', size: 14 },
                color: 'rgba(255,255,255,0.85)',
                maxRotation: 30,
                minRotation: 30,
                autoSkip: false,
              },
            },
            y: {
              min: 600,
              grid: { color: 'rgba(128,128,128,0.15)' },
              ticks: {
                font: { family: 'Arial', size: 13 },
                color: 'rgba(255,255,255,0.85)',
              },
              title: {
                display: true,
                text: 'Bayesian Elo',
                font: { family: 'Arial', size: 14 },
                color: 'rgba(255,255,255,0.9)',
              },
            },
          },
          layout: { padding: { top: 24 } },
        },
        plugins: [errorBarPlugin],
      });
    })();

    return () => {
      cancelled = true;
      chart?.destroy();
    };
  }, [includeEloNano]);

  return (
    <div className="w-full border-y border-[#373739] py-4 sm:rounded-2xl sm:border sm:p-8">
      <ChartHeader
        title="Amp Modeler Accuracy Test"
        description="Each model's output was compared against recordings of real gear using several error metrics (ESR, MAE, LOG_MEL, and MRSTFT). This chart shows Bayesian Elo ratings derived from ESR, where higher scores indicate lower measured error relative to the original amp, pedal, or signal chain."
      />
      <div className="w-full rounded-2xl border border-[#373739] p-6">
        <div className="text-lg font-bold text-white">Bayesian Elo Comparison (ESR)</div>
        <div className="mb-3 mt-1.5 text-base text-zinc-400">
          Mean ± 1 std across 39 tones, higher is better
        </div>
        <div className="relative h-[440px] w-full max-[500px]:h-[380px]">
          <canvas ref={eloRef} />
        </div>
        <div className="mt-5 flex flex-wrap items-center gap-x-8 gap-y-3 border-t border-[#373739] pt-4 text-xs">
          <div className="flex items-center gap-3">
            <span className="text-zinc-500">Include</span>
            <label className="flex cursor-pointer items-center gap-1.5 text-zinc-300">
              <input
                type="checkbox"
                checked={includeEloNano}
                onChange={(e) => setIncludeEloNano(e.target.checked)}
                className="accent-[#FFFF00]"
              />
              NAM A1-Nano
            </label>
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-end">
        <Logo style={{ height: 20, width: 'auto' }} />
      </div>
    </div>
  );
}
